import { useMemo, useCallback, useEffect, useState } from 'react';
import { Badge, Col, Form, Row } from 'antd';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from 'components';
import { DeleteIcon, EditIcon } from 'icons';
import { useTranslation } from 'react-i18next';

import { useApi } from 'hooks/useApi';

import Helpers from 'helpers';
import UIHelper from 'helpers/UIHelper';

import VacanciesAdapter from './vacancies.adapter';
import VacanciesApi from 'libs/api/cv/vacancies';
import { useRecoilValue } from 'recoil';
import { getShoppingCenters, getUser } from '../../../../hooks/useUser';
import { getShops, getInactiveShops } from '../../../../hooks/useShops';
import moment from 'moment';
import CVFilters from '../../../../libs/api/cv/cv';
import ErrorHelper from '../../../../helpers/ErrorHelper';
import { useNavigate } from '../../../../hooks/useNavigate';
import { EyeOutlined } from '@ant-design/icons';
import { useFilter } from '../../../../hooks/useFilter';

const statusColors = {
  Confermato: 'green',
  Rifiutato: 'red',
  Nuovo: 'yellow',
};

const api = new VacanciesApi();
const filtersApi = new CVFilters();

/**
 *
 * @param {boolean} isMainPage - true if the page loads all items, false if just one
 * @returns {{
 *  columns: any,
 *  data: any,
 *  singleItem: any,
 *  loading: boolean,
 *  updateInProcess: boolean,
 *  setUpdateInProcess: func,
 *  onDelete: func,
 *  onChangeTableFilter: func
 * }} state and callbacks for Vacancies page
 */

const useVacancies = (isMainPage = true, isEdit = false) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { id } = useParams();
  const user = useRecoilValue(getUser);
  const shoppingCenters = useRecoilValue(getShoppingCenters);
  const shops = useRecoilValue(getShops);
  const inactiveShops = useRecoilValue(getInactiveShops);
  const [errors, setErrors] = useState([]);
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [filtersData, setFiltersData] = useState({
    vacancy_categories: [],
    experience_years: [],
    birthday: [],
    contract_types: [],
    regions: [],
    status: [],
    vacancy_application_types: [],
    work_sector: [],
  });
  const { filterData } = useFilter();
  const {
    approveErrors,
    loading,
    data,
    singleItem,
    onDelete,
    onChangeTableFilter,
    updateInProcess,
    handleDownloadExcel,
    setUpdateInProcess,
    setApproveErrors,
  } = useApi({
    resourceKey: 'vacancies',
    singleResourceKey: 'vacancy',
    api,
    isMainPage,
    adapter: VacanciesAdapter,
  });

  const handleDownloadPdf = useCallback(
    async (fileName = 'vacancy_application.pdf') => {
      try {
        const response = await api.getPDF(id);
        Helpers.downloadFile(response, fileName);
      } catch (e) {
        UIHelper.serverError();
      }
    },
    [id],
  );
  const statuses = useMemo(
    () => ({
      status_new: t('Nuovo'),
      confirm: t('Confermato'),
      reject: t('Rifiutato'),
    }),
    [t],
  );

  const columns = useMemo(() => {
    return [
      {
        title: t('Outlet Village'),
        dataIndex: 'shopping_center',
        sorter: () => {},
      },
      {
        title: t('Insegna'),
        dataIndex: 'company_name',
        width: '25%',
        sorter: () => {},
      },
      {
        title: t('Titolo'),
        dataIndex: 'title',
        sorter: () => {},
      },
      {
        title: t('Settore'),
        dataIndex: 'vacancy_application_type',
        sorter: () => {},
      },
      // {
      //   title: t('Descrizione'),
      //   dataIndex: 'Descrizione',
      //   render: (data, row) => (
      //     <Row
      //       gutter={16}
      //       justify={'left'}
      //       onClick={(e) => e.stopPropagation()}
      //     >
      //       <Col>
      //         <Link to={`${pathname}/${row.uid}`}>Visualizza</Link>
      //       </Col>
      //     </Row>
      //   ),
      // },
      {
        title: t('Data pubblicazione'),
        dataIndex: 'created_at',
        sorter: () => {},
        render: (value) => moment(value).format('DD MMMM YYYY'),
      },
      {
        title: t('Scade il'),
        dataIndex: 'expired_at',
        sorter: () => {},
        render: (value) => moment(value, 'YYYY-MM-DD').format('DD MMMM YYYY'),
      },
      {
        title: t('Candidature annunci'),
        dataIndex: 'vacancy_application_size',
      },
      {
        title: t('Approvato'),
        dataIndex: 'status_title',
        render: (data, row) => <Badge color={statusColors[data]} text={data} />,
      },
      {
        title: t('Approvato S.A.'),
        dataIndex: 'director_approved_title',
        render: (data, row) => <Badge color={statusColors[data]} text={data} />,
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 350,
        fixed: 'right',
        render: (data, row) =>
          (row.can_update !== false ||
            row.can_destroy !== false ||
            row.can_view !== false) && (
            <Row
              gutter={16}
              justify={'center'}
              onClick={(e) => e.stopPropagation()}
            >
              {row.can_view !== false && (
                <Col>
                  <Link to={`${pathname}/${row.uid}/edit/view`}>
                    <Button icon={<EyeOutlined />} />
                  </Link>
                </Col>
              )}
              {row.can_view !== false && (
                <Col>
                  <Link
                    to={`/cv/vacancy_applications?vacancy_id=${row.id}&by_shopping_center=${row.shopping_center_id}`}
                  >
                    <Button>Visualizza candidati</Button>
                  </Link>
                </Col>
              )}
              {row.can_update !== false && (
                <Col>
                  <Link to={`${pathname}/${row.uid}/edit`}>
                    <Button type={'secondary'} icon={<EditIcon />} />
                  </Link>
                </Col>
              )}
              {row.can_update === false && row.can_approve === true && (
                <Col>
                  <Link to={`${pathname}/${row.uid}/edit`}>
                    <Button type={'secondary'} icon={<EditIcon />} />
                  </Link>
                </Col>
              )}
              {row.can_destroy !== false && (
                <Col>
                  <Link to={`${pathname}/${row.uid}/delete`}>
                    <Button type={'red'} icon={<DeleteIcon />} />
                  </Link>
                </Col>
              )}
            </Row>
          ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const filters = useMemo(
    () => [
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'by_shopping_center',
        values: filterData?.shopping_centers?.map((shopping_center) => ({
          title: shopping_center.title,
          value: shopping_center.value,
        })),
      },
      {
        title: t('Insegna'),
        type: 'select',
        name: 'by_company',
        isSearch: true,
        values: filterData?.companies?.map((company) => ({
          title: company.title,
          value: company.value,
        })),
        inactiveValues: filterData?.inactive_companies?.map((company) => ({
          title: company.title,
          value: company.value,
        })),
      },
      {
        title: t('Cerca'),
        type: 'input',
        name: 'by_title',
        values: [],
        isActiveSearch: true,
      },
      {
        title: t('Settore'),
        type: 'select',
        name: 'by_app_type',
        values: filtersData.vacancy_application_types.map((i) => ({
          value: i.value,
          title: i.title,
        })),
      },
      {
        title: t('Data pubblicazione'),
        type: 'date',
        name: 'date_from',
        values: [],
      },
    ],
    [t, filterData, filtersData],
  );

  useEffect(() => {
    (async () => {
      try {
        const filters = await filtersApi.list();
        setFiltersData(filters.data);
      } catch (e) {
        console.log(e);
      }
    })();
  }, []);

  const handleCreateOrUpdate = async (values) => {
    setUpdateInProcess(true);
    const data = {
      vacancy: {},
    };

    for (let key in values) {
      if (key === 'fields') {
        data.vacancy[key] = values[key].map((i) => {
          if (i.type === 1) {
            return {
              title: i.title,
              field_type: i.type,
            };
          } else {
            return {
              title: i.title,
              field_type: i.type,
              values: [
                i.value1,
                i.value2,
                i.value3,
                i.value4,
                i.value5,
                i.value6,
              ],
            };
          }
        });
      } else if (
        key === 'expired_at' ||
        key === 'user_created_at' ||
        key === 'published_at'
      ) {
        data.vacancy[key] = values[key]
          ? moment(values[key]).format('YYYY-MM-DD')
          : null;
      } else {
        data.vacancy[key] = values[key];
      }
    }
    try {
      if (!id) {
        await api.create({ data });
        navigate(UIHelper.getListRoute(pathname, 1));
      } else {
        await api.put(id, { data });
        navigate(UIHelper.getListRoute(pathname));
      }
    } catch (e) {
      const errors = e.response.data || [];
      setErrors(errors);
      form.setFields(ErrorHelper.getErrorsForForm(errors));
    } finally {
      setUpdateInProcess(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (id) {
        try {
          if (isEdit) {
            let fieldsValues = [];
            fieldsValues.push({
              name: 'fields',
              value: singleItem.vacancy_fields?.map((i) => ({
                title: i.title,
                type: i.vacancy_field_type_id,
                value1:
                  i?.vacancy_field_values &&
                  i?.vacancy_field_values[0] &&
                  i?.vacancy_field_values[0].title,
                value2:
                  i?.vacancy_field_values &&
                  i?.vacancy_field_values[1] &&
                  i?.vacancy_field_values[1].title,
                value3:
                  i?.vacancy_field_values &&
                  i?.vacancy_field_values[2] &&
                  i?.vacancy_field_values[2].title,
                value4:
                  i?.vacancy_field_values &&
                  i?.vacancy_field_values[3] &&
                  i?.vacancy_field_values[3].title,
                value5:
                  i?.vacancy_field_values &&
                  i?.vacancy_field_values[4] &&
                  i?.vacancy_field_values[4].title,
                value6:
                  i?.vacancy_field_values &&
                  i?.vacancy_field_values[5] &&
                  i?.vacancy_field_values[5].title,
              })),
            });

            for (let key in singleItem) {
              switch (key) {
                case 'fields': {
                  break;
                }
                case 'user_created_at':
                case 'published_at':
                case 'expired_at': {
                  fieldsValues.push({
                    name: key,
                    value: moment(singleItem[key], 'YYYY-MM-DD'),
                  });
                  break;
                }
                default: {
                  fieldsValues.push({
                    name: key,
                    value: singleItem[key],
                  });
                }
              }
            }
            form.setFields(fieldsValues);
          }
        } catch (e) {
          console.log(e);
        }
      }
    })();
  }, [singleItem]);

  const handleApprove = async (values, paramKey, backCount) => {
    const data = {
      comment: values.description,
    };
    switch (values.status) {
      case 'Approva': {
        if (!paramKey) {
          data.confirmed = true;
        } else {
          data[paramKey] = 'approva';
        }
        break;
      }
      case 'Rifiutato': {
        if (!paramKey) {
          data.refused = true;
        } else {
          data[paramKey] = 'respingi';
        }
        break;
      }
      case 'Escalate to admin': {
        data.to_admin = true;
        break;
      }
      case 'Annulla Intervento': {
        data.refused = true;
        break;
      }
      case 'publish': {
        data.publish = true;
        break;
      }
      case 'unpublish': {
        data.unpublish = true;
        break;
      }
      default:
        break;
    }
    setUpdateInProcess(true);
    try {
      await api.approve(id, data);
      navigate(UIHelper.getListRoute(pathname, backCount));
    } catch (e) {
      console.log(e);
      setApproveErrors(Object.values(e?.response?.data || {}));
      form.setFields([
        { name: 'expired_at', errors: Object.values(e?.response?.data || {}) },
      ]);
    } finally {
      setUpdateInProcess(false);
    }
  };

  return {
    columns,
    data,
    filters,
    singleItem,
    loading,
    onDelete,
    updateInProcess,
    onChangeTableFilter,
    handleDownloadExcel,
    handleDownloadPdf,
    handleApprove,
    shoppingCenters,
    shops,
    inactiveShops,
    filtersData,
    form,
    handleCreateOrUpdate,
    errors,
    user,
    approveErrors,
  };
};

export default useVacancies;
